// THE WORKSPACE APPS VIEW (/apps) — docs/APP_SPLIT.md §5.2,
// WS-STUDIO-SPLIT-WORKSPACE-APPS-FROM-SITES. Nika 2026-10-19: "workspace apps
// are pure adons on workspace". This is where a workspace sees its add-ons —
// the ones installed here, the ones its team built, the ones the App Store
// offers — and where each opens, changes and is managed. A website never
// appears here; it lives on the Websites board (/studio).
//
// Every function below reads root state only and writes nothing new: install,
// remove and share stay in their own flows (the App Store, /add-app). FA204 —
// each export carries its own helpers.

// el.call('appsHubModel', root) → { installed, team, store } — the rows the
// three sections render, every row keyed `app-…` (a data key never becomes an
// HTML tag) and carrying every field a row reads (no root fallback).
//   installed — one row per installed app (root.workspaceApps manifests, the
//               raw refs while a manifest is still loading, the takeover
//               module, installed AI-built apps); source 'store' | 'ai' |
//               'custom'; channel from the install ref.
//   team      — AI-built apps of this workspace (root._userApps) that are not
//               installed: they wait in /add-app.
//   store     — App Store apps (root._wsAppsCatalog) not installed here, first 6.
// No row carries a caption: no card here shows one (lane CAP, 2026-10-30). A
// store app's record `description` is its raw English line, so a card that
// one day shows a line reads THE store-copy resolver at render
// (`storeItemText`, as AddAppCatalogCard's Description does), never the row.
export function appsHubModel(root) {
  const r = root && typeof root === 'object' ? root : {}
  const lc = (v) => (v == null ? '' : String(v).trim().toLowerCase())
  const isObjectId = (v) => typeof v === 'string' && /^[a-f0-9]{24}$/i.test(v)
  const ws = r.workspace && typeof r.workspace === 'object' ? r.workspace : {}
  const settings = ws.settings && typeof ws.settings === 'object' ? ws.settings : {}
  const refs = Array.isArray(settings.workspaceApps) ? settings.workspaceApps : []
  const catalog = Array.isArray(r._wsAppsCatalog) ? r._wsAppsCatalog : []
  const catalogIds = new Set(catalog.map((a) => lc(a && a.id)).filter(Boolean))
  // An installed app is an App Store app when the loaded catalog lists it. A
  // PRIVATE system module (bookmarks-app on staging, 2026-10-21) is not in the
  // store: it read "App Store" and its Manage opened the general Marketplace
  // (no item page). Until the catalog has loaded (or when its read failed and
  // left it empty) a system app stays the provisional store guess.
  const catalogKnown = r._wsAppsCatalogLoaded === true && catalogIds.size > 0
  const storeSource = (id, owner) =>
    catalogIds.has(id) || (!catalogKnown && lc(owner) === 'system') ? 'store' : 'custom'
  const refById = new Map()
  for (const ref of refs) {
    if (!ref || typeof ref !== 'object') continue
    if (ref.type === 'extension' && ref.projectId) refById.set(lc(ref.projectId), ref)
    else if (ref.owner && ref.key) refById.set(lc(ref.owner) + '/' + lc(ref.key), ref)
  }
  const firstTile = (m) => {
    const tiles = Array.isArray(m && m.apps) ? m.apps : []
    const t = tiles.find((x) => x && x.route) || null
    if (t) return t
    const nav = Array.isArray(m && m.navbar) ? m.navbar.find((x) => x && x.path) : null
    return nav ? { title: nav.label, route: nav.path } : null
  }
  const channelOf = (ref) => (ref && ref.channel === 'development' ? 'development' : 'production')
  const installed = []
  const seen = new Set()
  const pushInstalled = (row) => {
    if (!row.id || seen.has(row.id)) return
    seen.add(row.id)
    installed.push(row)
  }
  const wp = r.workspaceProject
  for (const m of Array.isArray(r.workspaceApps) ? r.workspaceApps : []) {
    if (!m || typeof m.id !== 'string' || !m.id) continue
    const id = lc(m.id)
    const tile = firstTile(m) || {}
    const ai = isObjectId(m.id)
    const [owner, key] = ai ? ['', m.id] : m.id.split('/')
    const ref = refById.get(id) || null
    pushInstalled({
      key: 'app-' + id.replace(/[^a-z0-9]+/g, '-'),
      id,
      owner: owner || '',
      appKey: key || '',
      projectId: ai ? m.id : '',
      title: tile.title || (ref && ref.name) || key || m.id,
      icon: tile.icon || '',
      glyph: tile.glyph || '',
      route: tile.route || (ai ? '/x/' + m.id : ''),
      source: ai ? 'ai' : storeSource(id, owner),
      channel: channelOf(ref),
      installed: true
    })
  }
  // Refs whose manifest has not activated yet still count as installed.
  for (const [id, ref] of refById) {
    if (seen.has(id)) continue
    const ai = ref.type === 'extension'
    pushInstalled({
      key: 'app-' + id.replace(/[^a-z0-9]+/g, '-'),
      id,
      owner: ai ? '' : String(ref.owner || ''),
      appKey: ai ? String(ref.projectId) : String(ref.key || ''),
      projectId: ai ? String(ref.projectId) : '',
      title: ref.name || (ai ? '' : ref.key) || id,
      icon: '',
      glyph: '',
      route: ai ? '/x/' + ref.projectId : '',
      source: ai ? 'ai' : storeSource(id, ref.owner),
      channel: channelOf(ref),
      installed: true
    })
  }
  if (wp && typeof wp.id === 'string' && wp.id.indexOf('/') > 0) {
    const id = lc(wp.id)
    const [owner, key] = wp.id.split('/')
    const legacy = settings.workspaceModule
    pushInstalled({
      key: 'app-' + id.replace(/[^a-z0-9]+/g, '-'),
      id,
      owner,
      appKey: key,
      projectId: '',
      title: wp.name || (wp.config && wp.config.name) || key,
      icon: '',
      glyph: '',
      route: '/',
      source: catalogIds.has(id) ? 'store' : 'custom',
      channel: channelOf(legacy),
      installed: true
    })
  }
  const team = []
  for (const x of Array.isArray(r._userApps) ? r._userApps : []) {
    const pid = x && (x.id || x._id || x.projectId)
    if (!pid || seen.has(lc(pid))) continue
    seen.add(lc(pid))
    team.push({
      key: 'app-' + lc(pid),
      id: lc(pid),
      owner: '',
      appKey: String(pid),
      projectId: String(pid),
      title: x.name || x.title || String(pid),
      icon: '',
      glyph: '',
      route: '',
      source: 'ai',
      channel: 'development',
      installed: false
    })
  }
  // The workspace's OWN modules — a custom or forked module project of this
  // workspace that is not installed here and is not in the App Store (that
  // one waits in the store section): known by the server's app marker on the
  // project list (metadata.workspaceModule, server 4d77db631).
  const activeWsId = String(
    ws.id || ws._id || (r.jwtClaims && r.jwtClaims.active_workspace_id) || ''
  )
  const slugOf = (v) => {
    if (!v) return ''
    if (typeof v === 'string') return isObjectId(v) ? '' : v
    return v.slug || v.username || ''
  }
  for (const p of Array.isArray(r._projectsForActiveOrg) ? r._projectsForActiveOrg : []) {
    if (!p || !p.key) continue
    const m = p.metadata && typeof p.metadata === 'object' ? p.metadata : {}
    if (m.workspaceModule !== true) continue
    const w = p.workspace
    const pWs = String((w && (w.id || w._id)) || w || '')
    if (activeWsId && pWs && pWs !== activeWsId) continue
    const owner = slugOf(p.owner) || slugOf(w && w.organization)
    const key = String(p.key).split('.')[0]
    const id = lc(owner) + '/' + lc(key)
    if (!owner || seen.has(id) || catalogIds.has(id)) continue
    seen.add(id)
    team.push({
      key: 'app-' + id.replace(/[^a-z0-9]+/g, '-'),
      id,
      owner,
      appKey: key,
      projectId: '',
      title: p.name || key,
      icon: '',
      glyph: '',
      route: '',
      source: 'custom',
      channel: 'development',
      installed: false
    })
  }
  // THE STORE SECTION follows the workspace's preset (persona round 5, elena,
  // Medical: it offered Payroll, Invoices, Production and Products). A
  // workspace set up from a preset (`settings.preset.key`, not the 'custom'
  // answer) is offered what ITS preset names in the registry
  // (root._workspacePresets, GET /core/presets): the preset's `suggested`
  // apps first, then a preset app the apply left out — never an app the
  // preset does not name. Until the registry is read (or with no row for the
  // key) nothing is offered. No preset: the catalogue's first six.
  const applied = settings.preset && typeof settings.preset === 'object' ? settings.preset : null
  const presetKey = applied && typeof applied.key === 'string' ? applied.key.trim() : ''
  let offer = catalog
  if (presetKey && presetKey !== 'custom') {
    const registry = Array.isArray(r._workspacePresets) ? r._workspacePresets : []
    const preset = registry.find((p) => p && p.key === presetKey) || null
    const list = (v) => (Array.isArray(v) ? v : [])
    const named = preset ? [...list(preset.suggested), ...list(preset.apps)] : []
    const byId = new Map()
    for (const a of catalog) if (a && a.id && !byId.has(lc(a.id))) byId.set(lc(a.id), a)
    offer = []
    const picked = new Set()
    for (const id of named) {
      const a = byId.get(lc(id))
      if (!a || picked.has(lc(id))) continue
      picked.add(lc(id))
      offer.push(a)
    }
  }
  const store = []
  for (const a of offer) {
    if (!a || !a.id || seen.has(lc(a.id))) continue
    if (store.length >= 6) break
    store.push({
      key: 'app-' + lc(a.id).replace(/[^a-z0-9]+/g, '-'),
      id: lc(a.id),
      owner: String(a.owner || ''),
      appKey: String(a.key || ''),
      projectId: '',
      title: a.name || a.key || a.id,
      icon: '',
      glyph: '',
      route: '',
      source: 'store',
      channel: 'production',
      installed: false
    })
  }
  return { installed, team, store }
}

// el.call('appsHubLoad') — load the two lists the view needs from their own
// owners (the same reads /add-app runs): the AI-built apps
// (loadAddAppExtensions → root._userApps) and the App Store catalog
// (loadWorkspaceAppsCatalog → root._wsAppsCatalog). Installed apps are already
// on the root (the shell activates them at boot).
export function appsHubLoad() {
  const el = this
  const root = el && el.state && el.state.root
  if (!root) return
  try {
    el.call('loadAddAppExtensions', root)
  } catch (_) {}
  try {
    if (!root._wsAppsCatalogLoaded) el.call('loadWorkspaceAppsCatalog')
  } catch (_) {}
  // A preset workspace's store section reads its preset's suggestions from
  // the registry (appsHubModel): the store's reader, registered at boot by
  // marketplace/functions/eagerSlice.js. It never rejects.
  try {
    const ws = root.workspace
    const preset = ws && ws.settings && ws.settings.preset
    const key = preset && typeof preset === 'object' ? String(preset.key || '').trim() : ''
    if (key && key !== 'custom' && !Array.isArray(root._workspacePresets)) {
      Promise.resolve(el.call('workspacePresetsLoad')).catch(() => null)
    }
  } catch (_) {}
  // The workspace's own module projects (the "Built by your team" custom
  // rows): the home's org project list, read here when no home read it.
  try {
    if (!root._projectsForActiveOrg) el.call('canvasAnswerRead', el)
  } catch (_) {}
}

// el.call('appsHubOpen', row) — open an installed app where it lives.
export function appsHubOpen(row) {
  if (!row || !row.route) return
  this.call('openAppPage', row.route)
}

// el.call('appsHubEdit', row) — change an AI-built app in its builder
// (/add-app keys its thread on `_addAppProjectId`, set BEFORE the route — the
// launcher tile's Edit does the same, searchAppTileMenu.js).
export function appsHubEdit(row) {
  const root = this && this.state && this.state.root
  if (!root || !row || !row.projectId) return
  root.update({ _addAppProjectId: String(row.projectId) }, { preventFetch: true })
  this.call('openAppPage', '/add-app')
}

// el.call('appsHubManage', row) — an App Store app is managed in the App
// Store (its item deep link, from the App Store's one builder — the same item
// /add-app's catalog card and the search suggestions link). The workspace's
// own module has no store item: it is managed in /add-app, whose install form
// (owner / key) the card fills in first (`_wsAppsRefOwner` / `_wsAppsRefKey`,
// the form's own keys).
export function appsHubManage(row) {
  if (!row || !row.owner || !row.appKey) return
  if (row.source === 'custom') {
    const root = this && this.state && this.state.root
    if (root && typeof root.update === 'function' && !row.installed) {
      root.update(
        { _wsAppsRefOwner: String(row.owner), _wsAppsRefKey: String(row.appKey) },
        { preventFetch: true }
      )
    }
    this.call('openAppPage', '/add-app')
    return
  }
  // Its page in the catalogue's App Store tab (decision 7, ledger AS-3), in
  // the shell's pane like the navbar's Apps — /apps/store is no app-window
  // route, so a plain open landed a full page. The route is this hub's own;
  // the item is the App Store's (marketplace functions/appStoreItemLink.js,
  // registered at boot). An owner or a key that names no App Store item
  // opens nothing.
  const route = this.call('appStoreItemLink', row.owner, row.appKey, '/apps/store')
  if (!route) return
  this.call('openAppPage', route, { preferSlot: true })
}
